import prepareTranslate from '@/helpers/dictionary';
import { useForm } from '@mantine/form';
import { useState } from 'react';
import { createYearsArray } from '@/helpers/dates';
import { IconAlertTriangle, IconEye, IconInfoCircle } from '@tabler/icons-react';
import { FORM_ERRORS, MONTHS } from '@/helpers/enums';
import companyQuery from '@/api/queries/company';
import importRuleQuery from '@/api/queries/importRule';
import docsMutation from '@/api/mutitations/docs';
import {
  Grid,
  Select,
  Button,
  TextInput,
  Flex,
  Alert,
  LoadingOverlay,
} from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import routes from '@/routes';
import PageTemplate from '@/components/PageTemplate';
import Section from '@/components/Section';
import { useModalQueue } from '@/hooks/FileUploadResponseContext';
import Modal from '@/components/Modal/Index';
import styles from './style.module.css';

const formatThousands = (val) => {
  const stripped = val.replace(/,/g, '');
  if (!stripped) return '';
  const [intPart, decPart] = stripped.split('.');
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return decPart !== undefined ? `${formattedInt}.${decPart}` : formattedInt;
};

// Entering the values of a rule that has no file, by hand
const ManualImport = () => {
  const t = prepareTranslate();
  const navigate = useNavigate();
  const { showModal, closeModal } = useModalQueue();

  const [years] = useState(createYearsArray());
  const [showMonth, setShowMonth] = useState(true);
  const [showReferenceYear, setShowReferenceYear] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);

  const form = useForm({
    initialValues: {
      companyId: null,
      importRuleId: null,
      month: null,
      year: null,
      referenceYear: null,
      manualData: {},
    },
    validate: {
      companyId: (value) => (value === null ? FORM_ERRORS.required : null),
      importRuleId: (value) => (value === null ? FORM_ERRORS.required : null),
      month: (value) => (showMonth && value === null ? FORM_ERRORS.required : null),
      year: (value) => (value === null ? FORM_ERRORS.required : null),
      referenceYear: (value) => (showReferenceYear && value === null ? FORM_ERRORS.required : null),
      manualData: (value) => {
        if (!selectedRule?.manualFields?.length) return FORM_ERRORS.required;
        const allFilled = selectedRule.manualFields.every(
          (field) => value[field.key] && /^[\d,.]+$/.test(value[field.key]),
        );
        return allFilled ? null : FORM_ERRORS.required;
      },
    },
  });

  const { data: companies, isPending: companyPending } = companyQuery.useGetAll();
  const {
    mutateAsync: importManualMutate,
    isPending: importManualLoading,
  } = docsMutation.useImportManual();
  const {
    data: importRuleRes,
    isFetching: importRulePending,
  } = importRuleQuery.useGetByCompanyId({ companyId: form.getValues().companyId });

  const manualRules = (importRuleRes || []).filter((importRule) => importRule.manualEntry === true);

  const handleSubmit = async (data) => {
    await importManualMutate({
      importRuleId: data.importRuleId,
      year: data.year,
      month: data.month,
      manualData: data.manualData,
    })
      .then(() => {
        showModal({
          id: 'successImport',
          component: <Modal
            label={t('pages.import.title')}
            message={t('pages.import.notifications.success', [selectedRule?.ruleName])}
            opened={true}
            onClose={closeModal}
            withCloseButton={true}
          />,
        });
        form.setValues({ importRuleId: null, manualData: {} });
        setSelectedRule(null);
      })
      .catch(() => {
        showModal({
          id: 'example-modal',
          component: <Modal
            label={t('pages.import.notifications.internalError')}
            labelIcon={<IconAlertTriangle color='red'/>}
            opened={true}
            onClose={closeModal}
            withCloseButton={true}
          />,
        });
      });
  };

  const handleCompanySelect = () => {
    setSelectedRule(null);
    form.setValues({ month: null, year: null, manualData: {} });
    form.setFieldValue('importRuleId', null);
  };

  const handleImportRuleSelect = (value) => {
    const importRuleData = manualRules.find((importRule) => importRule._id === value);

    setSelectedRule(importRuleData);
    form.setFieldValue('manualData', Object.fromEntries(
      (importRuleData.manualFields || []).map((field) => [field.key, '']),
    ));
    setShowMonth(importRuleData.frequency !== 'yearly');
    setShowReferenceYear(Boolean(importRuleData.showReferenceYear));
  };

  const handleCancel = () => {
    form.reset();
    setSelectedRule(null);
  };

  const disabledInput = !form.isValid('companyId');
  const disabledSubmitButton = !form.isValid();

  const companiesOptions = companies ? (
    companies.map((company) => ({
      label: company.hebrew,
      value: company._id,
    }))) : (
    []
  );

  const importRuleOptions = manualRules.map((importRule) => ({
    label: importRule.ruleName,
    value: importRule._id,
  }));

  const noManualRules = Boolean(form.getValues().companyId)
    && !importRulePending && manualRules.length === 0;

  const actions = [
    <Button
      key={'files'}
      leftSection={<IconEye/>}
      onClick={() => navigate(routes.files.path)}>
      {t('pages.import.action')}
    </Button>,
  ];

  return (
    <PageTemplate
      title={routes.manualImports.name}
      actions={actions}
    >
      <div className={styles.wrapper}>
        <LoadingOverlay visible={companyPending || importRulePending} />
        <form onSubmit={form.onSubmit((values) => handleSubmit(values))}>
          <div className={styles.content}>
            <Section>
              <Grid gutter="xl" mt={16}>
                <Grid.Col span={{ base: 12, md: 3 }}>
                  <Select
                    data-autofocus
                    label={t('pages.import.form.company')}
                    data={companiesOptions}
                    clearable
                    key={form.key('companyId')}
                    onOptionSubmit={(option) => handleCompanySelect(option)}
                    {...form.getInputProps('companyId')}
                    required
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 3 }}>
                  <Select
                    label={t('pages.import.form.files')}
                    data={importRuleOptions}
                    clearable
                    disabled={disabledInput}
                    onOptionSubmit={(option) => handleImportRuleSelect(option)}
                    key={form.key('importRuleId')}
                    {...form.getInputProps('importRuleId')}
                    required
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 3 }}>
                  <Select
                    label={t('pages.import.form.year')}
                    data={years}
                    clearable
                    disabled={disabledInput}
                    key={form.key('year')}
                    {...form.getInputProps('year')}
                    required
                  />
                </Grid.Col>
                {showMonth && <Grid.Col span={{ base: 12, md: 3 }}>
                  <Select
                    label={t('pages.import.form.month')}
                    data={MONTHS}
                    clearable
                    disabled={disabledInput}
                    key={form.key('month')}
                    {...form.getInputProps('month')}
                    required
                  />
                </Grid.Col>}
                {showReferenceYear && <Grid.Col span={{ base: 12, md: 3 }}>
                  <Select
                    label={t('pages.import.form.referenceYear')}
                    data={years}
                    clearable
                    disabled={disabledInput}
                    key={form.key('referenceYear')}
                    {...form.getInputProps('referenceYear')}
                    required
                  />
                </Grid.Col>}
              </Grid>
            </Section>
            <Grid gutter='xl'>
              <Grid.Col span={{ base: 12 }}>
                {noManualRules && (
                  <Alert icon={<IconInfoCircle />} color="blue" mt="md">
                    {t('pages.import.form.noManualRules')}
                  </Alert>
                )}
                {selectedRule && (
                  <>
                    <Alert
                      icon={<IconInfoCircle />}
                      title={t('pages.import.form.noFile')}
                      color="blue"
                      mt="md"
                      mb="md"
                    >
                      {t('pages.import.form.noFileDescription')}
                    </Alert>
                    <Section>
                      <Grid gutter="xl" mt={16}>
                        {selectedRule.manualFields?.map((field) => (
                          <Grid.Col key={field.key} span={{ base: 12, md: 3 }}>
                            <TextInput
                              key={form.key(`manualData.${field.key}`)}
                              label={field.label}
                              {...form.getInputProps(`manualData.${field.key}`)}
                              onChange={(e) => {
                                const raw = e.target.value.replace(/,/g, '');
                                if (raw === '' || /^[\d.]*$/.test(raw)) {
                                  form.setFieldValue(`manualData.${field.key}`, formatThousands(raw));
                                }
                              }}
                            />
                          </Grid.Col>
                        ))}
                      </Grid>
                    </Section>
                  </>
                )}
              </Grid.Col>

              <Grid.Col span={{ base: 12 }}>
                <Flex justify="flex-start" gap="md">
                  <Button
                    type="submit"
                    loading={importManualLoading}
                    disabled={disabledSubmitButton}
                  >
                    {t('pages.import.form.action')}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleCancel}
                  >
                    {t('global.cancel')}
                  </Button>
                </Flex>
              </Grid.Col>
            </Grid>
          </div>
        </form>
      </div>
    </PageTemplate>
  );
};

export default ManualImport;
